// utils.js - pure helpers used everywhere. No DOM, no network, no storage
// side effects outside the ls* + downloadCsv functions.

export const UTILS = (() => {

  // ── HTML ESCAPING ────────────────────────────────────────────────────────────

  function escapeHtml(s) {
    if (s == null) return '';
    return String(s)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#39;');
  }

  // ── STRING HELPERS ───────────────────────────────────────────────────────────

  // Up to two initials, uppercase. "Juliette Neutelings" -> "JN".
  function initials(name) {
    return (name || '')
      .split(' ')
      .slice(0, 2)
      .map(w => w[0] || '')
      .join('')
      .toUpperCase();
  }

  // Canonical name key for the staff alias map: MUST mirror the SQL
  // lower(btrim(name)) used by the staff_canonical migration, or client-side
  // counts and server-side links drift apart. Deliberately NOT accent-stripped.
  function normName(s) {
    return (s || '').trim().toLowerCase();
  }

  // ── DATE HELPERS ─────────────────────────────────────────────────────────────

  // style: 'short' | 'medium' | 'long'. Anything else falls back to medium.
  function formatDate(date, style = 'medium') {
    if (!date) return '';
    const d = date instanceof Date ? date : new Date(date);
    const options = {
      short:  { day: 'numeric', month: 'short' },
      medium: { day: 'numeric', month: 'short', year: 'numeric' },
      long:   { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' },
    };
    return d.toLocaleDateString('en-GB', options[style] || options.medium);
  }

  function today() {
    return new Date().toISOString().split('T')[0];
  }

  /** '2025-2026' -> '2026-2027'. Unrecognised formats come back unchanged. */
  function nextSeason(season) {
    const m = String(season).match(/^(\d{4})-(\d{4})$/);
    if (!m) return season;
    return `${Number(m[1]) + 1}-${Number(m[2]) + 1}`;
  }

  /**
   * The age of a team, read off its id: 'U10B1' -> 'U10'.
   *
   * NEVER use teams.age_group for this. age_group is the EVALUATION MATRIX a
   * team is scored against, and 43 of the club's teams share the single value
   * "U11-U12" while actually spanning U7 to U12. Grouping or filtering by it
   * silently lumps seven-year-olds in with twelve-year-olds.
   *
   * Mirrors team_age_tag() in the database and practiceBandForTeamId(), so the
   * app and Postgres agree on what age a team is. Null for an unparseable id.
   */
  function teamAgeTag(teamId) {
    const m = /^U(\d+)/i.exec(String(teamId || ''));
    return m ? 'U' + parseInt(m[1], 10) : null;
  }

  /** Sort key for an age tag, so U9 comes before U10 rather than after it. */
  function teamAgeOrder(teamId) {
    const m = /^U(\d+)/i.exec(String(teamId || ''));
    return m ? parseInt(m[1], 10) : 999;
  }

  function addDays(date, days) {
    const d = new Date(date instanceof Date ? date : date);
    d.setDate(d.getDate() + days);
    return d.toISOString().split('T')[0];
  }

  // ── CODE GENERATION ──────────────────────────────────────────────────────────

  // crypto.getRandomValues - never Math.random for codes that gate access.
  // 32-char alphabet, 256 % 32 == 0 so byte-to-char is unbiased.
  // Pass opts.length to override (default 6) and opts.alphabet for a different set.
  function generateCode(prefix = '', opts = {}) {
    const alphabet = opts.alphabet || 'abcdefghijkmnpqrstuvwxyz23456789';
    const length   = opts.length   || 6;
    const bytes    = new Uint8Array(length);
    crypto.getRandomValues(bytes);
    const rand = Array.from(bytes, b => alphabet[b % alphabet.length]).join('');
    return prefix ? `${prefix}-${rand}` : rand;
  }

  function codeToUrl(code) {
    return `${window.location.origin}/?code=${encodeURIComponent(code)}`;
  }

  // ── SORTING ──────────────────────────────────────────────────────────────────

  function sortPlayersByName(players) {
    return [...players].sort((a, b) =>
      (a.name || '').localeCompare(b.name || '')
    );
  }

  // ── LOCAL STORAGE ────────────────────────────────────────────────────────────

  function lsSet(key, value) {
    try { localStorage.setItem(key, JSON.stringify(value)); } catch { /* full */ }
  }

  function lsGet(key, fallback = null) {
    try {
      const raw = localStorage.getItem(key);
      return raw !== null ? JSON.parse(raw) : fallback;
    } catch { return fallback; }
  }

  function lsRemove(key) {
    try { localStorage.removeItem(key); } catch { /* ignore */ }
  }

  // Draft key namespaces drafts by coach + player + team so two coaches sharing
  // a device can't overwrite each other's in-progress evaluations.
  function draftKey(playerId, teamId, code) {
    return `draft_${playerId}_${teamId}_${code}`;
  }

  // ── CSV PARSING ──────────────────────────────────────────────────────────────

  function parseCSV(csvText, delimiter = ',') {
    const lines = csvText.replace(/\r\n?/g, '\n').trim().split('\n');
    if (lines.length < 2) return [];
    const headers = parseCSVLine(lines[0], delimiter);
    return lines.slice(1)
      .filter(line => line.trim())
      .map(line => {
        const values = parseCSVLine(line, delimiter);
        return headers.reduce((obj, header, i) => {
          obj[header.trim()] = (values[i] || '').trim();
          return obj;
        }, {});
      });
  }

  function parseCSVLine(line, delimiter = ',') {
    const result = [];
    let current = '';
    let inQuotes = false;
    for (let i = 0; i < line.length; i++) {
      const char = line[i];
      if (char === '"' && line[i + 1] === '"') {
        current += '"'; i++;
      } else if (char === '"') {
        inQuotes = !inQuotes;
      } else if (char === delimiter && !inQuotes) {
        result.push(current); current = '';
      } else {
        current += char;
      }
    }
    result.push(current);
    return result;
  }

  // ── CSV DOWNLOAD ─────────────────────────────────────────────────────────────
  // Belgian Excel reads `;`-delimited UTF-8 by default; the BOM keeps accents
  // intact. Defaults pick the values that "just work" when the file is
  // double-clicked from Downloads.

  function csvEscape(value, delimiter = ';') {
    const str = value == null ? '' : String(value);
    if (str.includes(delimiter) || str.includes('"') || str.includes('\n')) {
      return '"' + str.replace(/"/g, '""') + '"';
    }
    return str;
  }

  function downloadCsv(rows, filename, opts = {}) {
    const delimiter = opts.delimiter || ';';
    const bom = opts.bom !== false;
    const body = rows.map(r => r.map(v => csvEscape(v, delimiter)).join(delimiter)).join('\n');
    const text = bom ? '﻿' + body : body;
    const blob = new Blob([text], { type: 'text/csv;charset=utf-8' });
    const url  = URL.createObjectURL(blob);
    const a    = document.createElement('a');
    a.href     = url;
    a.download = filename;
    a.click();
    URL.revokeObjectURL(url);
  }

  return {
    escapeHtml,
    initials,
    normName,
    formatDate,
    today,
    nextSeason,
    addDays,
    teamAgeTag,
    teamAgeOrder,
    generateCode,
    codeToUrl,
    sortPlayersByName,
    lsSet,
    lsGet,
    lsRemove,
    draftKey,
    parseCSV,
    csvEscape,
    downloadCsv,
  };

})();
